import { getSiloV2MarketEntry, type SiloHalfStatic } from '@1delta/data-sdk'
import { formatUnits } from 'viem'
import type { AdditionalYields } from '../../../types'
import type { GenericTokenList } from '../../../types/general'
import { toGenericPriceKey, toOracleKey } from '../../../assets'
import { createMarketUid } from '../../../utils'
import {
  SILO_V2_CALLS_PER_SIDE,
  SILO_V2_CALLS_PER_PAIR,
} from './publicCallBuild'
import { parseIRMConfig, calculateSiloRates } from './irmRates'

const ZERO_ADDRESS = '0x0000000000000000000000000000000000000000'

/**
 * Parse the dynamic state returned by `buildSiloV2LenderReserveCall` for a
 * single Silo v2 pair (one per-pair lender key `SILO_V2_<HEX>`).
 *
 * Result layout (6 slots):
 *   [totalAssets0, debtAssets0, depositAPR0, totalAssets1, debtAssets1, depositAPR1]
 *
 * Both sides are borrowable and serve as collateral for the OPPOSITE side, so
 * each silo emits a reserve entry whose `borrowingEnabled` and
 * `collateralActive` flags are both true. The collateral factor / LLTV used
 * for the reserve is the LLTV of the *other* silo (the side that holds the
 * collateral when borrowing this asset).
 *
 * Static metadata (decimals, oracle, lt, fees, share tokens) comes entirely
 * from `getSiloV2MarketEntry()` and never hits the chain on this code path.
 */
export const getSiloV2ReservesDataConverter = (
  lender: string,
  chainId: string,
  prices: { [asset: string]: number },
  additionalYields: AdditionalYields,
  tokenList: GenericTokenList = {},
): [(data: any[]) => { data: any; chainId: string } | undefined, number] => {
  const market = getSiloV2MarketEntry(chainId, lender)
  if (!market) return [() => undefined, 0]

  return [
    (data: any[]) => {
      if (!data || data.length !== SILO_V2_CALLS_PER_PAIR) return undefined

      const out: { [marketUid: string]: any } = {}
      const sides: Array<{ self: SiloHalfStatic; other: SiloHalfStatic }> = [
        { self: market.silo0, other: market.silo1 },
        { self: market.silo1, other: market.silo0 },
      ]

      // Coerce a multicall result slot into a bigint. `'0x'` (returned by
      // the multicall layer when an individual call reverted) becomes 0n —
      // we don't want one stale silo to nuke the whole pair.
      const toBigInt = (v: any): bigint => {
        if (v === undefined || v === null || v === '0x') return 0n
        if (typeof v === 'bigint') return v
        try {
          return BigInt(v as any)
        } catch {
          return 0n
        }
      }

      for (let s = 0; s < 2; s++) {
        const { self, other } = sides[s]
        const slot = s * SILO_V2_CALLS_PER_SIDE
        const totalAssetsRaw = toBigInt(data[slot])
        const debtAssetsRaw = toBigInt(data[slot + 1])
        const irmConfigRaw = data[slot + 2]

        const decimals = self.decimals
        const totalDeposits = Number(formatUnits(totalAssetsRaw, decimals))
        const totalDebt = Number(formatUnits(debtAssetsRaw, decimals))
        const totalLiquidity = totalDeposits - totalDebt
        const utilization = totalDeposits > 0 ? totalDebt / totalDeposits : 0

        // Derive rates from the IRM config + current utilization. If the
        // IRM call reverted or returned an unexpected shape, the parser
        // returns null and we fall back to 0% (better than poisoning the
        // whole batch with a thrown error).
        let depositRatePct = 0
        let variableBorrowRatePct = 0
        const irmParams = parseIRMConfig(irmConfigRaw, self)
        if (irmParams) {
          try {
            const rates = calculateSiloRates(utilization, irmParams)
            variableBorrowRatePct = rates.borrowRate
            depositRatePct = rates.depositRate
          } catch {
            // keep 0% defaults on compute failure
          }
        }

        const tokenAddrLc = self.token.toLowerCase()
        const assetMeta = tokenList[tokenAddrLc]
        const oracleKey =
          toOracleKey(assetMeta?.assetGroup ?? null) ||
          toGenericPriceKey(tokenAddrLc, chainId)
        const price = prices[oracleKey] ?? 0

        // In Silo v2, each silo's own lt/maxLtv describe how its DEPOSITS
        // work as collateral when borrowing the OTHER silo's asset. Both
        // are 1e18-scaled. A side with `lt == 0` is the "borrow-only" half
        // of an asymmetric pair: its deposits cannot be used as collateral.
        const selfLt = Number(self.lt) / 1e18
        const selfMaxLtv = Number(self.maxLtv) / 1e18
        const otherLt = Number(other.lt) / 1e18

        // `liquidationFee` is the seize penalty, WAD-scaled (1e18 = 100%) —
        // same scale as lt/maxLtv. Normalize to a plain fraction.
        const selfLiquidationPenalty = Number(self.liquidationFee ?? 0) / 1e18

        // collateralActive = this silo's deposits can be used as collateral
        //   for the OTHER side's borrows (self.lt > 0).
        // borrowingEnabled = some collateral side exists for THIS asset =
        //   counterparty (`other`) is usable as collateral (other.lt > 0).
        const collateralActive = selfLt > 0
        const borrowingEnabled = otherLt > 0

        const marketUid = createMarketUid(chainId, lender, self.silo)

        out[marketUid] = {
          marketUid,
          name: market.name
            ? `Silo V2 ${market.name} ${assetMeta?.symbol ?? ''}`
            : `Silo V2 ${assetMeta?.symbol ?? ''}`,
          poolId: self.silo.toLowerCase(),
          underlying: tokenAddrLc,
          asset: {
            ...(assetMeta ?? {}),
            chainId,
            decimals,
            address: tokenAddrLc,
            symbol: assetMeta?.symbol ?? self.symbol ?? '',
            name: assetMeta?.name ?? self.symbol ?? '',
          },
          // token amounts
          totalDeposits,
          totalDebt,
          totalDebtStable: 0,
          totalLiquidity,
          borrowLiquidity: totalLiquidity,
          // USD amounts
          totalDepositsUSD: totalDeposits * price,
          totalDebtUSD: totalDebt * price,
          totalDebtStableUSD: 0,
          totalLiquidityUSD: totalLiquidity * price,
          borrowLiquidityUSD: totalLiquidity * price,
          utilization,
          depositRate: depositRatePct,
          variableBorrowRate: variableBorrowRatePct,
          stableBorrowRate: 0,
          intrinsicYield:
            additionalYields?.intrinsicYields?.[assetMeta?.assetGroup as any] ??
            0,
          rewards: undefined,
          decimals,
          // Each Silo v2 per-pair lender represents exactly ONE pair, so
          // there is only one collateral counterparty (the OTHER silo) and
          // a single config entry. Key it as `'0'` to match the Morpho
          // convention (`config[0]` is the single mode with the market's
          // LLTV) — the counterparty silo address is still surfaced via
          // `params.counterpartySilo`.
          config: {
            0: {
              category: 0,
              borrowCollateralFactor: selfMaxLtv,
              collateralFactor: selfLt,
              borrowFactor: 1,
              liquidationPenalty: selfLiquidationPenalty,
              closeFactor: 1,
              collateralDisabled: !collateralActive,
              debtDisabled: !borrowingEnabled,
            },
          },
          closeFactor: 1,
          collateralActive,
          borrowingEnabled,
          depositsEnabled: true,
          hasStable: false,
          isActive: true,
          isFrozen: false,
          params: {
            metadata: {
              silo: self.silo,
              counterpartySilo: other.silo,
              siloConfig: market.siloConfig,
              oracle: self.solvencyOracle || ZERO_ADDRESS,
              irm: self.interestRateModel || ZERO_ADDRESS,
              shareTokens: {
                collateral: self.collateralShareToken,
                protected: self.protectedShareToken,
                debt: self.debtShareToken,
              },
              fees: {
                dao: self.daoFee,
                deployer: self.deployerFee,
                liquidation: self.liquidationFee,
                flashloan: self.flashloanFee,
              },
            },
          },
        }
      }

      return { data: out, chainId }
    },
    SILO_V2_CALLS_PER_PAIR,
  ]
}
